/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Stack } from '@fluentui/react/lib/Stack';
import { ITextFieldStyleProps, ITextFieldStyles } from '@fluentui/react/lib/TextField';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisTextField } from 'src/core/components/LuisTextField';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { DemoPageAgreement } from 'src/languageStudio/appShell/prebuiltDemos/components/DemoPageAgreement';
import { qnaDemoSupportedRegions } from 'src/languageStudio/appShell/prebuiltDemos/utils/demosSupportedRegion';
import { textAnalyticsPricingLink } from 'src/languageStudio/appShell/prebuiltDemos/utils/demosSupportLinks';
import { validateLanguageStudioResource } from 'src/languageStudio/core/utils/demosUtils';
import { LuisText } from '../../../../core/components/LuisText';

export type QuestionInputStepProps = { question: string; runQuestion: () => void; onQuestionChange: (newQuestion: string) => void };

const messages = defineMessages({
    askQuestion: {
        id: 'languageStudio.questionAnswersDemoPage.askQuestion',
        defaultMessage: 'Ask a question'
    },
    enterQuestion: {
        id: 'languageStudio.questionAnswersDemoPage.enterQuestion',
        defaultMessage: 'Enter a question'
    }
});

const questionFieldStyles = classNamesFunction<ITextFieldStyleProps, ITextFieldStyles>()({ root: { minWidth: '300px' } });

export const QuestionInputStep = observer((props: QuestionInputStepProps) => {
    const { question, runQuestion, onQuestionChange } = props;

    const { localize } = useLocalization();
    const { rootStore } = useGlobalState();
    const { csAccountStore } = rootStore.azureStore.session;

    return (
        <Stack horizontalAlign="start" tokens={{ childrenGap: 20 }}>
            <LuisText as="h2" variant="xLarge" fontWeight={600}> {localize(messages.askQuestion)} </LuisText>
            <LuisTextField
                value={question}
                styles={questionFieldStyles}
                label={localize(messages.enterQuestion)}
                placeholder={localize(messages.enterQuestion)}
                onChange={(_, newValue) => onQuestionChange(newValue)}
                disabled={
                    !validateLanguageStudioResource({ localize, resource: csAccountStore, supportedRegions: qnaDemoSupportedRegions })
                        .isValid
                }
            />

            <DemoPageAgreement
                onRunDemoClick={runQuestion}
                isRunDisabled={!question.length}
                featureName={'Question answering'}
                acceptedResourceType="TextAnalytics"
                pricingLink={textAnalyticsPricingLink}
                supportedRegions={qnaDemoSupportedRegions}
            />
        </Stack>
    );
});
